import { logger } from "@/utils/logger";
import { captureMessage, addBreadcrumb } from "@/lib/sentry";

export interface TourEvent {
  type: "start" | "complete" | "skip" | "step" | "error";
  stage?: string;
  step?: number;
  totalSteps?: number;
  error?: string;
  timestamp: string;
}

export class TourLogger {
  private static logEvent(event: TourEvent) {
    const message = `Tour ${event.type}: ${event.stage || 'unknown'} ${event.step !== undefined ? `step ${event.step}/${event.totalSteps}` : ''}`;
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.log('[Tour]', message, event);
    }
    
    // Add breadcrumb to Sentry
    addBreadcrumb(message, {
      category: 'tour',
      level: event.type === 'error' ? 'error' : 'info',
      data: event,
    });
    
    // Send to production analytics (you can integrate with PostHog, Mixpanel, etc. here)
    if (!import.meta.env.DEV) {
      // Example: window.analytics?.track('tour_event', event);
    }
  }

  static tourStarted(stage: string, totalSteps: number) {
    this.logEvent({
      type: "start",
      stage,
      totalSteps,
      timestamp: new Date().toISOString(),
    });
  }

  static tourCompleted(stage: string) {
    this.logEvent({
      type: "complete",
      stage,
      timestamp: new Date().toISOString(),
    });
  }

  static tourSkipped(stage: string, step: number, totalSteps: number) {
    this.logEvent({
      type: "skip",
      stage,
      step,
      totalSteps,
      timestamp: new Date().toISOString(),
    });
  }

  static stepCompleted(stage: string, step: number, totalSteps: number) {
    this.logEvent({
      type: "step",
      stage,
      step,
      totalSteps,
      timestamp: new Date().toISOString(),
    });
  }

  static tourError(stage: string, error: string) {
    this.logEvent({
      type: "error",
      stage,
      error,
      timestamp: new Date().toISOString(),
    });
    
    // Also log to standard error logger
    logger.error(`Tour error in ${stage}:`, new Error(error));
  }

  static missingElement(selector: string, stage: string) {
    const error = `Missing tour element: ${selector}`;
    this.tourError(stage, error);
    captureMessage(error, "warning");
  }
}
